/**
 * PHARMAKON — paid modules catalogue
 *
 * Ported from the design's config/modules.config.js. Per the design's choice
 * every module is locked with an upgrade CTA — this demonstrates the
 * ModuleGateCallout component pattern.
 */

export const MODULE_CATALOGUE = [
  {
    code: "VIGNOCR",
    nom: "VignOCR",
    description:
      "OCR de vignettes pharmaceutiques pour saisie automatique des achats.",
    benefits: [
      "Scan vignette → ligne d'achat",
      "Reconnaissance num. lot & péremption",
      "Réduction saisie de 70%",
    ],
    prix_mensuel: 2900,
    active: false,
    locked: true,
  },
  {
    code: "FACTUROCR",
    nom: "FactureOCR",
    description:
      "OCR de factures fournisseur — saisie automatique des en-têtes et lignes.",
    benefits: [
      "PDF/scan → facture brouillon",
      "Reconnaissance fournisseur",
      "90% temps gagné",
    ],
    prix_mensuel: 3900,
    active: false,
    locked: true,
  },
  {
    code: "AIDE_DECISION",
    nom: "Aide à la décision",
    description:
      "Pré-commandes multi-fournisseurs avec scoring automatique des offres.",
    benefits: [
      "Fan-out vers N fournisseurs",
      "Score composite UG / délai / prix",
      "Comparatif côte-à-côte",
    ],
    prix_mensuel: 4900,
    active: false,
    locked: true,
  },
  {
    // Canonical module — consolidates the legacy "Assistant IA" (CHATBOT) and the
    // Dermo-Conseiller into a single product. CHATBOT is now an alias of this
    // (see MODULE_ALIASES below): a pharmacy that paid for CHATBOT is treated as
    // entitled to AIDE_AU_CONSEIL everywhere on the client.
    code: "AIDE_AU_CONSEIL",
    nom: "Aide au Conseil",
    description:
      "Conseiller dermo-cosmétique : conseil post-vente assisté par IA + assistant de vente guidé (Dermo-Conseiller).",
    benefits: [
      "Conseil post-vente assisté par IA",
      "Assistant de vente guidé (Dermo-Conseiller)",
      "Interactions & contre-indications",
    ],
    prix_mensuel: 2400,
    active: false,
    locked: true,
  },
  {
    code: "DATA_SYNTHESE",
    laboratoireOnly: true, // laboratory product — hidden from pharmacy accounts
    nom: "Data Synthèse",
    description:
      "Synthèse de données pour laboratoires partenaires (anonymisée).",
    benefits: [
      "Tendances par DCI",
      "Données wilaya/région",
      "Export sécurisé",
    ],
    prix_mensuel: 6900,
    active: false,
    locked: true,
  },
];

// ─── Module aliases (entitlement consolidation) ───────────────────────────────
// Mirror of the server's MODULE_ALIASES (apps/server/src/config/constants.js):
// the deprecated legacy `CHATBOT` ("Assistant IA") maps onto the canonical
// `AIDE_AU_CONSEIL` ("Aide au Conseil") — the SAME product. A pharmacy that
// paid for CHATBOT is therefore treated as entitled to AIDE_AU_CONSEIL on the
// client too. The server already expands user.modules with the canonical code
// (auth.service getMe → resolveAliasedModules), so in practice user.modules
// already carries AIDE_AU_CONSEIL; this client map keeps the helper correct even
// against an un-expanded legacy list (cached session, mocks).
export const MODULE_ALIASES = Object.freeze({
  CHATBOT: "AIDE_AU_CONSEIL",
});

/**
 * Does this owned-module list entitle the holder to `moduleCode`?
 * Resolves aliases: holding the alias (e.g. CHATBOT) satisfies a check for the
 * canonical module (AIDE_AU_CONSEIL). The wildcard "*" satisfies everything.
 *
 * @param {string[]} owned       user.modules (or user.licence.modules)
 * @param {string}   moduleCode  the module being gated on
 * @returns {boolean}
 */
export function pharmacyHasModule(owned, moduleCode) {
  if (!Array.isArray(owned)) return false;
  if (owned.includes("*")) return true;
  if (owned.includes(moduleCode)) return true;
  // The holder owns an alias whose canonical target is the gated module.
  return owned.some((code) => MODULE_ALIASES[code] === moduleCode);
}

/**
 * The module list carried by a session user, or `null` when the session does
 * not say (a cached user from before the modules flattening). Tolerates the
 * two shapes the codebase produces: `user.modules` (flattened by /auth/me)
 * and `user.licence.modules`. Shared by ModuleGate and the menu locks
 * (ux/01 §6.4 « Locked item », AUD-NAV-10).
 * @param {any} user
 * @returns {string[] | null}
 */
export function userModules(user) {
  if (!user) return null;
  if (Array.isArray(user.modules)) return user.modules;
  if (Array.isArray(user.licence?.modules)) return user.licence.modules;
  return null;
}
